import React from "react";
import { Avatar, Tag } from "antd";
import Moment from "react-moment";
import PropTypes from "prop-types";
import { useUserContext } from "../../contexts/UserContext";
import { InlineEditableInput } from "../utils/InlineEditableInput";
import { AffaireService } from "../../services/AffaireService";
import { catchError } from "../../services/DaoService";
import { PrioriteService } from "../../services/PrioriteService";
import UserService from "../../services/UserService";
import styles from "./AffaireDetails.module.scss";
import { affairesState } from "../../state/actions/affairesState";

const AffaireDetails = ({ affaire, isReadOnly = true }) => {
  const { currentUser } = useUserContext();
  const [priorites, setPriorites] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [users, setUsers] = React.useState([]);

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      PrioriteService.getInstance().all(),
      UserService.getInstance().all(),
    ])
      .then(([p, u]) => { setPriorites(p.data); setUsers(u.data); })
      .catch(catchError)
      .finally(() => setLoading(false));
  }, []);

  const canManageAffaire = affaire?.user_id === currentUser?.id || affaire?.responsable_id === currentUser?.id;
  const canEditOwner = !isReadOnly && (affaire?.user_id === currentUser?.id || affaire?.responsable_id === currentUser?.id);

  const handleFieldChange = (value, fieldName, record) => {
    AffaireService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then(r => affairesState.updateAffaireInList(r.data))
      .catch(catchError);
  };

  if (loading) return null;

  const fields = [
    {
      label: 'Nom',
      value: (
        <InlineEditableInput
          canEdit={!isReadOnly && canManageAffaire}
          fieldName="nom"
          type="text"
          record={affaire}
          value={affaire?.nom}
          onChange={handleFieldChange}
        >
          {affaire?.nom ?? '—'}
        </InlineEditableInput>
      ),
      full: true,
    },
    {
      label: 'Responsable',
      value: (
          <InlineEditableInput
          canEdit={!isReadOnly && canManageAffaire}
          fieldName="responsable_id"
          type="select"
          options={users.map(u => ({
            value: u.id,
            label: (
              <span className={styles.userField}>
                <Avatar size={18} src={u.avatar}>{u.name?.[0]}</Avatar>
                <span className={styles.userFieldName}>{u.name}</span>
              </span>
            ),
            full_name: u.name,
          }))}
          optionFilterProp="full_name"
          record={affaire}
          value={affaire?.responsable_id}
          onChange={handleFieldChange}
        >
          <span className={styles.userField}>
            <Avatar size={18} src={affaire?.responsable?.avatar}>{affaire?.responsable?.name?.[0]}</Avatar>
            <span className={styles.userFieldName}>{affaire?.responsable?.name ?? '—'}</span>
          </span>
        </InlineEditableInput>
      ),
    },
    {
      label: 'Propriétaire',
      value: (
        <InlineEditableInput
          canEdit={canEditOwner}
          fieldName="user_id"
          type="select"
          options={users.map(u => ({
            value: u.id,
            label: (
              <span className={styles.userField}>
                <Avatar size={18} src={u.avatar}>{u.name?.[0]}</Avatar>
                <span className={styles.userFieldName}>{u.name}</span>
              </span>
            ),
            full_name: u.name,
          }))}
          optionFilterProp="full_name"
          record={affaire}
          value={affaire?.user_id}
          onChange={handleFieldChange}
        >
          <span className={styles.userField}>
            <Avatar size={18} src={affaire?.user?.avatar}>{affaire?.user?.name?.[0]}</Avatar>
            <span className={styles.userFieldName}>{affaire?.user?.name ?? '—'}</span>
          </span>
        </InlineEditableInput>
      ),
    },
    {
      label: 'Type',
      value: affaire?.modele?.nom
        ? <Tag color={affaire.modele.couleur} className={styles.tag}>{affaire.modele.nom}</Tag>
        : '—',
    },
    {
      label: 'Priorité',
      value: (
          <InlineEditableInput
          canEdit={!isReadOnly && canManageAffaire}
          fieldName="priorite_id"
          type="select"
          options={priorites.map(p => ({ value: p.id, label: p.nom }))}
          record={affaire}
          value={affaire?.priorite_id}
          onChange={handleFieldChange}
        >
          {affaire?.priorite?.nom
            ? <Tag color={affaire.priorite.couleur} className={styles.tag}>{affaire.priorite.nom}</Tag>
            : '—'}
        </InlineEditableInput>
      ),
    },
    {
      label: 'Étape',
      value: <Tag color="blue" className={styles.tag}>{affaire?.etape?.nom ?? '—'}</Tag>,
    },
    {
      label: 'Date limite',
      value: (
          <InlineEditableInput
          canEdit={!isReadOnly && canManageAffaire}
          fieldName="date_limite"
          type="date"
          record={affaire}
          value={affaire?.date_limite}
          onChange={handleFieldChange}
        >
          {affaire?.date_limite
            ? <Tag color={affaire.retard ? 'red' : 'default'} className={styles.tag}>
                <Moment format="DD/MM/YYYY">{affaire.date_limite}</Moment>
              </Tag>
            : '—'}
        </InlineEditableInput>
      ),
    },
    {
      label: 'Budget',
      value: (
          <InlineEditableInput
          type="number"
          canEdit={!isReadOnly && canManageAffaire}
          fieldName="budget"
          record={affaire}
          value={affaire?.budget}
          onChange={handleFieldChange}
        >
          <strong>{affaire?.budget}</strong>{' '}{affaire?.monnaie?.symbole}
        </InlineEditableInput>
      ),
    },
    {
      label: 'Description',
      value: (
          <InlineEditableInput
          type="textarea"
          canEdit={!isReadOnly && canManageAffaire}
          fieldName="description"
          record={affaire}
          value={affaire?.description}
          onChange={handleFieldChange}
        >
          <div className={styles.description}>{affaire?.description ?? '—'}</div>
        </InlineEditableInput>
      ),
      full: true,
    },
  ];

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <p className={styles.sidebarTitle}>Informations</p>
      </div>
      <div className={styles.fieldList}>
        {fields.map(f => (
          <div key={f.label} className={styles.field}>
            <div className={styles.fieldLabel}>{f.label}</div>
            <div className={styles.fieldValue}>{f.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

AffaireDetails.propTypes = {
  affaire: PropTypes.object,
  isReadOnly: PropTypes.bool,
};

export default AffaireDetails;
